import { useEffect, useState } from 'react';

import getRemoteSettings from './getRemoteSettings';

type CreateRemoteSettingHookParams<TSetting, TSelected> = {
  name: string;
  defaultValue: TSetting;
  validate: (settings: unknown) => boolean;
  /**
   * Maps the raw setting to what the hook returns. Select a primitive: state
   * holds the selected value, so React's `Object.is` bail-out skips re-renders
   * when the resolved setting equals the default — a fresh-but-equal settings
   * object would re-render every subscriber for nothing.
   */
  select: (settings: TSetting) => TSelected;
};

/**
 * Builds a module-cached remote-setting gate: one SDK fetch per session
 * (concurrent mounts share the in-flight promise via `getSetting`), a
 * `useSetting` hook for components, and the `getSetting` promise for
 * non-React callers.
 */
const createRemoteSettingHook = <TSetting, TSelected>({
  name,
  defaultValue,
  validate,
  select,
}: CreateRemoteSettingHookParams<TSetting, TSelected>) => {
  let cachedValue: TSetting | undefined;
  let pendingFetch: Promise<TSetting> | undefined;

  const getSetting = (): Promise<TSetting> => {
    pendingFetch ??= getRemoteSettings<TSetting>({
      name,
      tag: name,
      defaultValue,
      validate,
    }).then((value) => {
      cachedValue = value;
      return value;
    });
    return pendingFetch;
  };

  const useSetting = (): TSelected => {
    const [selected, setSelected] = useState<TSelected>(() =>
      select(cachedValue ?? defaultValue)
    );

    useEffect(() => {
      // Re-read the cache instead of early-returning on it: the shared fetch
      // may have resolved between this component's render (which seeded state
      // from an empty cache) and this effect running, and nothing else would
      // ever deliver the resolved value to this instance.
      if (cachedValue !== undefined) {
        setSelected(select(cachedValue));
        return;
      }
      getSetting().then((value) => setSelected(select(value)));
    }, []);

    return selected;
  };

  return { useSetting, getSetting };
};

export default createRemoteSettingHook;
